import React, { useState, useEffect, useMemo } from 'react';
import { Clock, MapPin, Coffee, Gavel, Car, Brain, Users, Building, Calendar, Sparkles, Download } from 'lucide-react';
import { AgendaSesion } from '../types/congreso.types';

interface ScheduleSectionProps {
  agenda: AgendaSesion[];
  programaPdfUrl?: string | null;
  anioCongreso?: number | string | null;
}

export const ScheduleSection: React.FC<ScheduleSectionProps> = ({
  agenda,
  programaPdfUrl,
  anioCongreso
}) => {
  const anio = anioCongreso || '2026';
  const rutaPdf = programaPdfUrl || `/docs/${anio}/PROGRAMA.pdf`;
  const nombreDescarga = `PROGRAMA_CONGRESO_${anio}.pdf`;

  // Extraer días únicos de la agenda
  const diasDisponibles = useMemo(() => {
    return Array.from(new Set(agenda.map(a => a.diaNombre))).filter(Boolean);
  }, [agenda]);

  const [diaSeleccionado, setDiaSeleccionado] = useState<string>('');
  const [filtroBloque, setFiltroBloque] = useState<'TODOS' | 'Mañana' | 'Tarde'>('TODOS');

  // Día activo garantizado
  const diaActivo = diaSeleccionado || (diasDisponibles.length > 0 ? diasDisponibles[0] : 'TODOS');

  const getIconForType = (icono: string | null, tipoCodigo: string) => {
    switch (icono?.toLowerCase() || tipoCodigo?.toLowerCase()) {
      case 'coffee':
      case 'coffee_break':
        return <Coffee className="w-5 h-5 text-amber-500" />;
      case 'gavel':
        return <Gavel className="w-5 h-5 text-brand-navy" />;
      case 'car':
      case 'sorteo':
        return <Car className="w-5 h-5 text-emerald-600" />;
      case 'brain':
        return <Brain className="w-5 h-5 text-purple-600" />;
      case 'users':
      case 'panel':
        return <Users className="w-5 h-5 text-brand-cyan" />;
      default:
        return <Building className="w-5 h-5 text-brand-navy" />;
    }
  };

  const sesionesFiltradas = useMemo(() => {
    return agenda.filter(s => {
      const matchDia = diaActivo === 'TODOS' || diasDisponibles.length === 0 || s.diaNombre === diaActivo;
      const matchBloque = filtroBloque === 'TODOS' || s.bloque === filtroBloque;
      return matchDia && matchBloque;
    });
  }, [agenda, diaActivo, diasDisponibles.length, filtroBloque]);

  return (
    <section id="agenda" className="py-24 bg-slate-50 relative border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sky-50 border border-sky-200 text-brand-cyan text-xs font-bold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Cronograma Oficial
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-brand-navy tracking-tight">
            Programa & Ejes Temáticos
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600">
            Jornadas magistrales de capacitación, ponencias y mesas de debate en Mar del Plata.
          </p>
        </div>

        {/* Si hay agenda cargada, renderizamos los controles interactivos y las tarjetas */}
        {agenda.length > 0 ? (
          <>
            {/* Barra superior de Controles de Jornada, Descarga y Bloque */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4 mb-12 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
              {/* Tabs por Día con opción "Todos" (Tarea 7) */}
              <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                <button
                  onClick={() => setDiaSeleccionado('TODOS')}
                  className={`px-5 py-2.5 rounded-xl font-heading font-bold text-sm tracking-wide transition-all ${
                    diaSeleccionado === 'TODOS'
                      ? 'bg-brand-navy text-white shadow-md shadow-brand-navy/20'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Todos
                </button>
                {diasDisponibles.map((dia) => (
                  <button
                    key={dia}
                    onClick={() => setDiaSeleccionado(dia)}
                    className={`px-5 py-2.5 rounded-xl font-heading font-bold text-sm tracking-wide transition-all ${
                      diaActivo === dia
                        ? 'bg-brand-navy text-white shadow-md shadow-brand-navy/20'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {dia}
                  </button>
                ))}
              </div>

              {/* Botón de Descarga de PDF Oficial (Tarea 6) y Filtros de Bloque */}
              <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto justify-between lg:justify-end">
                {/* Botón Descargar PDF */}
                <a
                  href={rutaPdf}
                  download={nombreDescarga}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-cyan hover:bg-sky-500 text-white font-bold text-xs sm:text-sm transition-all shadow-md shadow-brand-cyan/20 hover:scale-105"
                  title="Descargar programa oficial en PDF"
                >
                  <Download className="w-4 h-4" />
                  <span>Descargar Programa (PDF)</span>
                </a>

                {/* Filtros por Turno Horario */}
                <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl text-xs font-semibold">
                  {(['TODOS', 'Mañana', 'Tarde'] as const).map((bloque) => (
                    <button
                      key={bloque}
                      onClick={() => setFiltroBloque(bloque)}
                      className={`px-3 py-1.5 rounded-lg transition-colors ${
                        filtroBloque === bloque
                          ? 'bg-white text-brand-navy shadow-sm font-bold'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {bloque === 'TODOS' ? 'Todo' : bloque}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Listado de Sesiones con Diferenciación Cromática por Turno (Tarea 7) */}
            <div className="space-y-4">
              {sesionesFiltradas.length > 0 ? (
                sesionesFiltradas.map((sesion) => {
                  const esTarde = sesion.bloque?.toLowerCase() === 'tarde';
                  return (
                    <div
                      key={sesion.idSesion}
                      className={`bg-white rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 border shadow-sm hover:shadow-md transition-all ${
                        esTarde
                          ? 'border-slate-200 border-l-4 border-l-brand-indigo'
                          : 'border-slate-200 border-l-4 border-l-brand-cyan'
                      }`}
                    >
                      {/* Horario, Turno y Tipo */}
                      <div className="flex items-center gap-4 md:w-64 flex-shrink-0">
                        <div className={`p-3.5 rounded-2xl border ${
                          esTarde
                            ? 'bg-indigo-50 border-indigo-200 text-brand-indigo'
                            : 'bg-sky-50 border-sky-200 text-brand-cyan'
                        }`}>
                          {getIconForType(sesion.iconoTipo, sesion.tipoSesionCodigo)}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5 text-brand-navy font-heading font-black text-lg">
                            <Clock className={`w-4 h-4 ${esTarde ? 'text-brand-indigo' : 'text-brand-cyan'}`} />
                            <span>{sesion.horaInicio} - {sesion.horaFin} hs</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md ${
                              esTarde
                                ? 'bg-indigo-100 text-brand-indigo'
                                : 'bg-sky-100 text-brand-cyan'
                            }`}>
                              {sesion.bloque || (esTarde ? 'Tarde' : 'Mañana')}
                            </span>
                            <span className="text-xs text-slate-500 font-medium">
                              {sesion.diaNombre}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Contenido Principal */}
                      <div className="flex-1">
                        <h3 className="text-xl sm:text-2xl font-bold text-brand-navy mb-2">
                          {sesion.titulo}
                        </h3>
                        {sesion.descripcion && (
                          <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                            {sesion.descripcion}
                          </p>
                        )}

                        {/* Expositores asociados a la sesión */}
                        {sesion.expositores && sesion.expositores.length > 0 && (
                          <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-slate-100">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                              Disertantes:
                            </span>
                            <div className="flex flex-wrap items-center gap-2.5">
                              {sesion.expositores.map((exp) => (
                                <div key={exp.idExpositor} className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-200">
                                  {exp.fotoUrl ? (
                                    <img
                                      src={exp.fotoUrl}
                                      alt={exp.nombreCompleto}
                                      className="w-6 h-6 rounded-full object-cover border border-brand-cyan"
                                    />
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center text-[10px] font-bold text-brand-cyan">
                                      {exp.nombreCompleto.charAt(0)}
                                    </div>
                                  )}
                                  <span className="text-xs font-semibold text-slate-800">
                                    {exp.nombreCompleto}
                                  </span>
                                  {exp.rolEnSesion && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white text-slate-500 border border-slate-200 font-medium">
                                      {exp.rolEnSesion}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Sala */}
                      {sesion.sala && (
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 md:self-center bg-slate-50 px-3.5 py-2 rounded-xl border border-slate-200 flex-shrink-0">
                          <MapPin className="w-4 h-4 text-brand-cyan" />
                          <span>{sesion.sala}</span>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12 text-slate-500 bg-white rounded-2xl border border-slate-200 shadow-sm">
                  No hay actividades programadas para el filtro seleccionado.
                </div>
              )}
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="bg-white rounded-3xl p-12 text-center max-w-2xl mx-auto flex flex-col items-center border border-slate-200 shadow-sm">
            <div className="w-16 h-16 rounded-2xl bg-sky-50 flex items-center justify-center border border-sky-200 text-brand-cyan mb-6">
              <Calendar className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-bold text-brand-navy mb-3">
              Cronograma en Elaboración
            </h3>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-6">
              El comité organizador está coordinando las disertaciones y mesas de debate para este congreso.
            </p>
            <div className="inline-flex items-center gap-2 text-xs text-brand-cyan font-bold bg-sky-50 px-4 py-2 rounded-full border border-sky-200">
              <Sparkles className="w-4 h-4" />
              Próximamente grilla completa de actividades
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
